import { Geolocation } from "@capacitor/geolocation";
import { getPlatform } from "@/lib/platform";

export type AppPermissionState = "prompt" | "granted" | "denied" | "unsupported" | "checking";

export interface AppPosition {
  timestamp: number;
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
  };
}

export interface AppPositionOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  minimumUpdateInterval?: number;
}

type BrowserPermissionState = "granted" | "denied" | "prompt";

const isNative = () => getPlatform().isNative;

const normalizePermission = (state?: string | null): AppPermissionState => {
  if (state === "granted") return "granted";
  if (state === "denied") return "denied";
  if (state === "prompt" || state === "prompt-with-rationale") return "prompt";
  return "prompt";
};

const normalizePositionError = (error: unknown) => {
  const raw = typeof error === "object" && error !== null ? error as { code?: number; message?: string } : {};
  const message = raw.message || "Kunde inte hämta plats";
  const lower = message.toLowerCase();

  if (raw.code === 1 || lower.includes("permission") || lower.includes("denied")) {
    return { code: 1, message: "permission_denied" as const };
  }
  if (raw.code === 3 || lower.includes("timeout")) {
    return { code: 3, message: "location_timeout" as const };
  }
  if (raw.code === 2 || lower.includes("unavailable") || lower.includes("disabled") || lower.includes("settings")) {
    return { code: 2, message: "location_unavailable" as const };
  }
  return { code: raw.code ?? 0, message };
};

const toAppPosition = (position: any): AppPosition => ({
  timestamp: position.timestamp ?? Date.now(),
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    altitude: position.coords.altitude ?? null,
    altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
    heading: position.coords.heading ?? position.coords.trueHeading ?? position.coords.magneticHeading ?? position.coords.course ?? null,
    speed: position.coords.speed ?? null,
  },
});

export async function getGeolocationPermissionState(): Promise<AppPermissionState> {
  if (typeof navigator === "undefined") return "unsupported";

  if (isNative()) {
    try {
      const status = await Geolocation.checkPermissions();
      return normalizePermission(status.location);
    } catch {
      return "prompt";
    }
  }

  if (!("geolocation" in navigator)) return "unsupported";
  if (!navigator.permissions?.query) return "prompt";

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return normalizePermission(status.state as BrowserPermissionState);
  } catch {
    return "prompt";
  }
}

export async function requestGeolocationPermissions(): Promise<AppPermissionState> {
  if (typeof navigator === "undefined") return "unsupported";

  if (isNative()) {
    try {
      const status = await Geolocation.requestPermissions();
      return normalizePermission(status.location);
    } catch (error) {
      return normalizePositionError(error).code === 1 ? "denied" : "prompt";
    }
  }

  return getGeolocationPermissionState();
}

export async function getCurrentAppPosition(options: AppPositionOptions = {}): Promise<AppPosition> {
  if (typeof navigator === "undefined") {
    throw new Error("Geolocation not supported");
  }

  if (isNative()) {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: options.enableHighAccuracy,
      timeout: options.timeout,
      maximumAge: options.maximumAge,
      minimumUpdateInterval: options.minimumUpdateInterval,
    });
    return toAppPosition(position);
  }

  if (!navigator.geolocation) {
    throw new Error("Geolocation not supported");
  }

  return new Promise<AppPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(toAppPosition(position)),
      (error) => reject(error),
      options,
    );
  });
}

export async function watchAppPosition(
  options: AppPositionOptions,
  callback: (position: AppPosition | null, error?: { code?: number; message: string }) => void,
): Promise<string | number> {
  if (typeof navigator === "undefined") {
    throw new Error("Geolocation not supported");
  }

  if (isNative()) {
    return Geolocation.watchPosition(
      {
        enableHighAccuracy: options.enableHighAccuracy,
        timeout: options.timeout,
        maximumAge: options.maximumAge,
        minimumUpdateInterval: options.minimumUpdateInterval,
      },
      (position, err) => {
        if (err) {
          callback(null, normalizePositionError(err));
          return;
        }
        callback(position ? toAppPosition(position) : null);
      },
    );
  }

  if (!navigator.geolocation) {
    throw new Error("Geolocation not supported");
  }

  return navigator.geolocation.watchPosition(
    (position) => callback(toAppPosition(position)),
    (error) => callback(null, normalizePositionError(error)),
    options,
  );
}

export async function clearAppWatch(id: string | number | null) {
  if (id == null) return;

  if (isNative()) {
    await Geolocation.clearWatch({ id: String(id) });
    return;
  }

  if (typeof navigator !== "undefined" && navigator.geolocation) {
    navigator.geolocation.clearWatch(Number(id));
  }
}

export function subscribeToGeolocationPermission(
  listener: (state: AppPermissionState) => void,
): (() => void) | null {
  if (typeof navigator === "undefined" || isNative() || !navigator.permissions?.query) {
    return null;
  }

  let active = true;
  let cleanup: (() => void) | null = null;

  void navigator.permissions.query({ name: "geolocation" }).then((status) => {
    if (!active) return;
    const onChange = () => listener(normalizePermission(status.state as BrowserPermissionState));
    status.addEventListener("change", onChange);
    cleanup = () => status.removeEventListener("change", onChange);
  }).catch(() => {
    cleanup = null;
  });

  return () => {
    active = false;
    cleanup?.();
  };
}

export function mapGeolocationError(error: unknown) {
  return normalizePositionError(error);
}